/**
 * Validates a blob PDF API response and triggers a file download.
 * Prevents saving HTML/JSON error pages as .pdf when the server or proxy misbehaves.
 */
export async function downloadPdfFromResponse(response, defaultFilename) {
  const blob = response.data instanceof Blob
    ? response.data
    : new Blob([response.data]);

  const headerSlice = blob.slice(0, 5);
  const headerText = await headerSlice.text();

  if (!headerText.startsWith('%PDF')) {
    const bodyText = await blob.text();
    let message = 'Failed to download PDF';
    try {
      const json = JSON.parse(bodyText);
      message = json.message || message;
    } catch {
      const trimmed = bodyText.trim().toLowerCase();
      if (trimmed.startsWith('<!doctype') || trimmed.startsWith('<html')) {
        message = 'Server returned a web page instead of a PDF. Check API URL / proxy settings.';
      } else if (/<[a-z][\s\S]*>/i.test(bodyText)) {
        message = 'PDF generation failed — received HTML instead of a PDF file.';
      }
    }
    throw new Error(message);
  }

  let filename = defaultFilename;
  const cd = response.headers?.['content-disposition'];
  if (cd) {
    const match = /filename\*?=(?:UTF-8''|")?([^";\n]+)/i.exec(cd);
    if (match?.[1]) {
      filename = decodeURIComponent(match[1].replace(/"/g, '').trim());
    }
  }

  const pdfBlob = new Blob([blob], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(pdfBlob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename.endsWith('.pdf') ? filename : `${filename}.pdf`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
}
